import { Building2, CheckCircle2, KeyRound, LogIn, LogOut, Mail } from "lucide-react";
import { type FormEvent, useState } from "react";
import {
  loginAdmin,
  logoutAdmin,
  registerAdmin,
  resendAdminConfirmation,
  switchAdminOrg
} from "../../lib/auth-api";
import {
  type AccessStorage,
  type AdminAuthSession,
  clearAuthSession,
  describeAccess,
  getSavedAdminEmail,
  normalizeAdminEmail,
  saveAuthSession
} from "../../lib/access";

type AuthMode = "login" | "register";

export interface AuthOrgProvisioningPanelProps {
  storage?: AccessStorage;
  session?: AdminAuthSession | null;
  onOrgChange?: (orgId: string, session: AdminAuthSession | null) => void;
  onSessionChange?: (session: AdminAuthSession | null) => void;
}

export function AuthOrgProvisioningPanel({
  storage,
  session,
  onOrgChange,
  onSessionChange
}: AuthOrgProvisioningPanelProps) {
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState(() => getSavedAdminEmail(storage));
  const [password, setPassword] = useState("");
  const [orgName, setOrgName] = useState("My organization");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{ message: string; url?: string } | null>(null);

  async function submitAccess(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);
    setConfirmation(null);

    try {
      if (mode === "register") {
        const result = await registerAdmin({
          email: normalizeAdminEmail(email),
          password,
          orgName
        });
        setConfirmation({
          message: confirmationMessage(result),
          url: result.confirmationUrl
        });
        setPassword("");
        return;
      }

      const nextSession = saveAuthSession(await loginAdmin({
        email: normalizeAdminEmail(email),
        password
      }), storage);
      setPassword("");
      onSessionChange?.(nextSession);
      onOrgChange?.(nextSession.activeOrg.orgId, nextSession);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }

  async function resendConfirmation() {
    setPending(true);
    setError(null);
    setConfirmation(null);

    try {
      const result = await resendAdminConfirmation(normalizeAdminEmail(email));
      setConfirmation({
        message: result.emailConfirmed
          ? `${result.email} is already confirmed.`
          : confirmationMessage(result),
        url: result.confirmationUrl
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }

  async function switchOrg(orgId: string) {
    setPending(true);
    setError(null);

    try {
      const nextSession = saveAuthSession(await switchAdminOrg(orgId, session?.sessionKey), storage);
      onSessionChange?.(nextSession);
      onOrgChange?.(nextSession.activeOrg.orgId, nextSession);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }

  async function signOut() {
    setPending(true);
    setError(null);

    try {
      await logoutAdmin(session?.sessionKey);
    } catch {
      // Local session cleanup still needs to happen if the remote session is already expired.
    } finally {
      clearAuthSession(storage);
      onSessionChange?.(null);
      onOrgChange?.("", null);
      setPending(false);
    }
  }

  if (session) {
    return (
      <section className="form-panel access-panel" aria-labelledby="access-panel-title">
        <div className="card-heading">
          <KeyRound size={20} aria-hidden="true" />
          <div>
            <h2 id="access-panel-title">Admin access</h2>
            <p>{describeAccess(session)}</p>
          </div>
        </div>

        <div className="access-panel-form">
          <label>
            Organization
            <select
              value={session.activeOrg.orgId}
              onChange={(event) => void switchOrg(event.target.value)}
              disabled={pending || session.memberships.length < 2}
            >
              {session.memberships.map((membership) => (
                <option value={membership.orgId} key={membership.orgId}>
                  {membership.orgName} ({membership.role})
                </option>
              ))}
            </select>
          </label>

          {error && <div className="banner error" role="alert">{error}</div>}

          <div className="banner neutral access-summary">
            <Building2 size={18} aria-hidden="true" />
            <span>{session.activeOrg.orgId}</span>
          </div>

          <div className="header-controls access-actions">
            <button className="icon-button" type="button" onClick={() => void signOut()} disabled={pending}>
              <LogOut size={17} aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="form-panel access-panel" aria-labelledby="access-panel-title">
      <div className="card-heading">
        <KeyRound size={20} aria-hidden="true" />
        <div>
          <h2 id="access-panel-title">Admin access</h2>
          <p>Email and password access for standalone Glimbr Voice organizations.</p>
        </div>
      </div>

      <div className="segmented-control" role="tablist" aria-label="Authentication mode">
        <button type="button" className={mode === "login" ? "active" : undefined} onClick={() => setMode("login")}>
          <LogIn size={16} aria-hidden="true" />
          Sign in
        </button>
        <button type="button" className={mode === "register" ? "active" : undefined} onClick={() => setMode("register")}>
          <Mail size={16} aria-hidden="true" />
          Create
        </button>
      </div>

      <form className="access-panel-form" onSubmit={submitAccess}>
        <label>
          Email
          <input
            autoComplete="email"
            inputMode="email"
            name="email"
            placeholder="admin@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        <label>
          Password
          <input
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            name="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        {mode === "register" && (
          <label>
            Organization name
            <input
              autoComplete="organization"
              name="orgName"
              value={orgName}
              onChange={(event) => setOrgName(event.target.value)}
            />
          </label>
        )}

        {error && <div className="banner error" role="alert">{error}</div>}
        {confirmation && (
          <div className="banner success access-summary" role="status">
            <CheckCircle2 size={18} aria-hidden="true" />
            <span>
              {confirmation.message}
              {confirmation.url ? ` ${confirmation.url}` : ""}
            </span>
          </div>
        )}

        <div className="header-controls access-actions">
          <button className="primary-button" type="submit" disabled={pending}>
            {mode === "login" ? <LogIn size={17} aria-hidden="true" /> : <Mail size={17} aria-hidden="true" />}
            {mode === "login" ? "Sign in" : "Create account"}
          </button>
          <button className="icon-button" type="button" onClick={() => void resendConfirmation()} disabled={pending || !email}>
            <Mail size={17} aria-hidden="true" />
            Resend
          </button>
        </div>
      </form>
    </section>
  );
}

function confirmationMessage(result: { email: string; emailSendStatus?: string; emailSendReason?: string }): string {
  if (result.emailSendStatus === "sent") {
    return `Confirmation email sent to ${result.email}.`;
  }

  if (result.emailSendStatus === "failed") {
    return result.emailSendReason || `Confirmation email could not be sent to ${result.email}.`;
  }

  return `Account created. Confirm ${result.email} before signing in.`;
}
